/**
 * Integrytas Group — Email Distribution System (EDS)
 * Page: EDS.DASHBOARD  —  Send Dashboard
 *
 * High-density Acumatica-style delivery stats dashboard.
 *
 * Layout:
 *   ┌──────────────────────────────────────────────────────────┐
 *   │  SUMMARY BAR  (aggregate across all campaigns shown)     │
 *   ├──────────────────────────────────────────────────────────┤
 *   │  FILTER TOOLBAR  (search, status, date range)            │
 *   ├──────────────────────────────────────────────────────────┤
 *   │  PER-CAMPAIGN GRID  (sortable, paginated)                │
 *   └──────────────────────────────────────────────────────────┘
 *
 * Stats columns per campaign:
 *   Name | Status | Recipients | Sent | Delivered % | Bounce % | Opens | Clicks | Opt-Outs | Scheduled | Completed
 */

import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { listCampaignStats, type CampaignStats } from '../../services/eds/EdsSendService';
import { StatusBadge, type StatusVariant } from '../../components/common/StatusBadge';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { SkeletonRows } from '../../components/common/SkeletonRows';
import { EmptyState } from '../../components/common/EmptyState';
import { SortableHeader } from '../../components/common/SortableHeader';
import { compareStrings, useSort } from '../../lib/use_sort';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, GRID_TABLE_CLASS } from '../../components/personalization';
import { usePersonalization } from '../../lib/use_personalization';
import type { GridColumnDef } from '../../components/personalization';
import type { AuthSession } from '../../types/auth';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type StatSortKey =
  | 'campaign_name'
  | 'status'
  | 'total_recipients'
  | 'sent_count'
  | 'delivered_pct'
  | 'bounce_pct'
  | 'open_rate_pct'
  | 'opted_out_count';

// Campaign-status → localized label + badge variant (§17.B11: never render the
// stored enum literal; the label goes through i18n with a neutral fallback).
const STATUS_META: Record<string, { labelKey: string; label: string; variant: StatusVariant }> = {
  draft:                 { labelKey: 'eds.status.draft',                 label: 'Draft',                 variant: 'neutral' },
  scheduled:             { labelKey: 'eds.status.scheduled',             label: 'Scheduled',             variant: 'info' },
  queued:                { labelKey: 'eds.status.queued',                label: 'Queued',                variant: 'info' },
  sending:               { labelKey: 'eds.status.sending',               label: 'Sending',               variant: 'info' },
  sent:                  { labelKey: 'eds.status.sent',                  label: 'Sent',                  variant: 'success' },
  completed:             { labelKey: 'eds.status.completed',             label: 'Completed',             variant: 'success' },
  completed_with_errors: { labelKey: 'eds.status.completed_with_errors', label: 'Completed with errors', variant: 'warning' },
  paused:                { labelKey: 'eds.status.paused',                label: 'Paused',                variant: 'warning' },
  cancelled:             { labelKey: 'eds.status.cancelled',             label: 'Cancelled',             variant: 'danger' },
  failed:                { labelKey: 'eds.status.failed',                label: 'Failed',                variant: 'danger' },
};

// Clearly-numeric data columns — right-aligned tabular figures.
const NUM_COLS = new Set(['total_recipients', 'sent_count', 'clicked_count', 'opted_out_count', 'failed_count']);

const FILTER_KEY = 'eds.dashboard.filter';
const STATUS_KEY = 'eds.dashboard.status';

const GRID_COLS: GridColumnDef[] = [
  { key: 'campaign_name',    header: 'Campaign',    defaultWidth: 220 },
  { key: 'status',           header: 'Status',      defaultWidth: 100 },
  { key: 'total_recipients', header: 'Recipients',  defaultWidth: 100 },
  { key: 'sent_count',       header: 'Sent',        defaultWidth: 80  },
  { key: 'delivered_pct',    header: 'Delivered %', defaultWidth: 130 },
  { key: 'bounce_pct',       header: 'Bounce %',    defaultWidth: 130 },
  { key: 'open_rate_pct',    header: 'Open Rate',   defaultWidth: 130 },
  { key: 'clicked_count',    header: 'Clicks',      defaultWidth: 80  },
  { key: 'opted_out_count',  header: 'Opt-Outs',    defaultWidth: 90  },
  { key: 'failed_count',     header: 'Failed',      defaultWidth: 80  },
];

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface KpiCardProps {
  label: string;
  value: string | number;
  sub?: string;
  accent?: boolean;
  warning?: boolean;
  danger?: boolean;
}

function KpiCard({ label, value, sub, accent, warning, danger }: KpiCardProps) {
  const borderColor = danger   ? '#FCA5A5'
                    : warning  ? '#FCD34D'
                    : accent   ? '#00529B'
                    : '#E5E7EB';
  const valueColor  = danger   ? '#DC2626'
                    : warning  ? '#D97706'
                    : accent   ? '#00529B'
                    : '#1F2937';

  return (
    <div
      style={{
        border:        `1px solid ${borderColor}`,
        borderTop:     `3px solid ${borderColor}`,
        background:    '#fff',
        borderRadius:  4,
        padding:       '10px 14px',
        minWidth:      110,
        flex:          1,
      }}
    >
      <div style={{ fontSize: 11, color: '#6B7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: valueColor, lineHeight: 1.2 }}>
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>{sub}</div>
      )}
    </div>
  );
}

function PctBar({ pct, color = '#00529B' }: { pct: number; color?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ flex: 1, background: '#F3F4F6', borderRadius: 2, height: 6, overflow: 'hidden' }}>
        <div style={{ width: `${Math.min(pct, 100)}%`, height: '100%', background: color, borderRadius: 2 }} />
      </div>
      <span style={{ fontSize: 11, color: '#4B5563', width: 34, textAlign: 'right', flexShrink: 0 }}>
        {pct}%
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function EdsDashboardPage({ session }: { session: AuthSession }) {
  return (
    <PersonalizationProvider pageCode="EDS.DASHBOARD" session={session}>
      <EdsDashboardPageInner session={session} />
    </PersonalizationProvider>
  );
}

function EdsDashboardPageInner({ session }: { session: AuthSession }) {
  const { t } = useTranslation();
  const { isPersonalizing } = usePersonalization();
  const [stats, setStats]         = useState<CampaignStats[]>([]);
  const [busy, setBusy]           = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const [filter, setFilter]       = useState(() => sessionStorage.getItem(FILTER_KEY) ?? '');
  const [statusFilter, setStatusFilter] = useState(() => sessionStorage.getItem(STATUS_KEY) ?? '');
  const sort = useSort<StatSortKey>('eds.dashboard.sort', { key: 'campaign_name', dir: 'asc' });
  const detail = useExpandedRows<string>();

  // §17.B13 related-children: read-only stats row, no child collections loaded.
  // TODO: wire relations if per-campaign detail is ever pre-loaded here.
  const buildRelatedGroups = (_id: string): RelatedChildGroup[] => [];
  const hasRelated = (id: string) => buildRelatedGroups(id).some(g => g.items.length > 0);

  useEffect(() => { sessionStorage.setItem(FILTER_KEY, filter); }, [filter]);
  useEffect(() => { sessionStorage.setItem(STATUS_KEY, statusFilter); }, [statusFilter]);

  const reload = useCallback(async () => {
    setBusy(true); setError(null);
    try {
      const data = await listCampaignStats();
      setStats(data);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { void reload(); }, [reload]);

  // ── Filter + sort ─────────────────────────────────────────────────────────

  const filtered = useMemo(() => {
    const base = stats.filter(s => {
      const matchText = !filter.trim() ||
        s.campaign_name.toLowerCase().includes(filter.toLowerCase());
      const matchStatus = !statusFilter || s.status.toLowerCase() === statusFilter.toLowerCase();
      return matchText && matchStatus;
    });

    return sort.sorted(base, (a, b, key) => {
      if (
        key === 'total_recipients' || key === 'sent_count' ||
        key === 'delivered_pct'   || key === 'bounce_pct'  ||
        key === 'open_rate_pct'   || key === 'opted_out_count'
      ) {
        return (a[key] as number) - (b[key] as number);
      }
      return compareStrings(
        String((a as unknown as Record<string, unknown>)[key] ?? ''),
        String((b as unknown as Record<string, unknown>)[key] ?? ''),
      );
    });
  }, [stats, filter, statusFilter, sort]);

  // ── Aggregate summary ──────────────────────────────────────────────────────

  const agg = useMemo(() => {
    const totalSent      = filtered.reduce((s, r) => s + r.sent_count,      0);
    const totalDelivered = filtered.reduce((s, r) => s + r.delivered_count,  0);
    const totalBounced   = filtered.reduce((s, r) => s + r.bounced_count,    0);
    const totalOpened    = filtered.reduce((s, r) => s + r.opened_count,     0);
    const totalOptOut    = filtered.reduce((s, r) => s + r.opted_out_count,  0);

    return {
      campaigns:    filtered.length,
      sent:         totalSent,
      deliveredPct: totalSent > 0 ? Math.round((totalDelivered / totalSent) * 100) : 0,
      bouncePct:    totalSent > 0 ? Math.round((totalBounced   / totalSent) * 100) : 0,
      openRate:     totalDelivered > 0 ? Math.round((totalOpened / totalDelivered) * 100) : 0,
      optOuts:      totalOptOut,
    };
  }, [filtered]);

  // ── Unique statuses for filter dropdown ───────────────────────────────────

  const statusOptions = useMemo(() => {
    const unique = [...new Set(stats.map(s => s.status))].sort();
    return [{ value: '', label: t('eds.email_log.all_statuses') }, ...unique.map(v => ({ value: v, label: v }))];
    // t is the stable i18next translator; recomputing on its identity is unnecessary.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stats]);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="flex h-full flex-col">

      {/* ── Summary bar ── */}
      <div
        style={{
          display:        'flex',
          gap:            8,
          padding:        '8px 12px',
          background:     '#F3F4F6',
          borderBottom:   '1px solid #E5E7EB',
          flexShrink:     0,
          flexWrap:       'wrap',
        }}
      >
        <KpiCard label={t('eds.dashboard.kpi.campaigns', 'Campaigns')}    value={agg.campaigns}                                            accent />
        <KpiCard label={t('eds.dashboard.kpi.emails_sent', 'Emails Sent')}  value={agg.sent.toLocaleString()}                               accent />
        <KpiCard label={t('eds.dashboard.kpi.delivered', 'Delivered')}    value={`${agg.deliveredPct}%`}  sub={t('eds.dashboard.kpi.of_sent', 'of sent')}                   accent={agg.deliveredPct >= 95} />
        <KpiCard label={t('eds.dashboard.kpi.bounce_rate', 'Bounce Rate')}  value={`${agg.bouncePct}%`}     sub={t('eds.dashboard.kpi.of_sent', 'of sent')}                   danger={agg.bouncePct > 5} warning={agg.bouncePct > 2} />
        <KpiCard label={t('eds.dashboard.kpi.open_rate', 'Open Rate')}    value={`${agg.openRate}%`}       sub={t('eds.dashboard.kpi.of_delivered', 'of delivered')}              accent={agg.openRate >= 20} />
        <KpiCard label={t('eds.dashboard.kpi.opt_outs', 'Opt-Outs')}     value={agg.optOuts.toLocaleString()} sub={t('eds.dashboard.kpi.across_shown_campaigns', 'across shown campaigns')} danger={agg.optOuts > 50} />
      </div>

      {/* ── Filter toolbar ── */}
      <div className="flex h-10 shrink-0 items-center gap-2 border-b border-csm-border bg-white px-3">
        <input
          type="text"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder={t('eds.dashboard.search_placeholder', 'Search campaigns…')}
          className="h-7 w-64 rounded border border-csm-border px-2 text-csm-md focus:border-primary focus:outline-none"
        />
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="h-7 rounded border border-csm-border px-2 text-csm-sm focus:outline-none"
        >
          {statusOptions.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <button
          onClick={() => void reload()}
          className="h-7 rounded border border-csm-border bg-white px-2 text-csm-sm hover:bg-csm-surface-alt"
        >
          {t('common.refresh')}
        </button>
        {busy  && <span className="text-csm-xs text-csm-muted">{t('common.loading')}</span>}
        <span className="text-csm-xs text-csm-muted">
          {t('eds.dashboard.campaign_count', '{{count}} campaign', { count: filtered.length })}
        </span>
        <div className="ml-auto"><PersonalizationToggle /></div>
      </div>
      {error && (
        <div className="shrink-0 px-3 pt-2">
          <ErrorBanner
            message={t('eds.dashboard.load_failed', "Couldn't load campaign statistics.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void reload()}
            details_label={t('common.details', 'Details')}
          />
        </div>
      )}
      {isPersonalizing && (
        <div className="shrink-0 border-b border-primary/20 bg-primary/5 px-3 py-1 text-csm-xs text-primary">
          {t('eds.dashboard.personalization_banner', 'Personalization mode — configure columns, then click the icon to save and exit.')}
        </div>
      )}

      {/* ── Grid ── */}
      <div className="flex-1 overflow-auto">
        {busy && stats.length === 0 ? (
          <SkeletonRows rows={8} />
        ) : (
        <PersonalizedGrid componentKey="grid" columns={GRID_COLS}>
          {(visibleCols, widths) => (
        <table className={GRID_TABLE_CLASS}>
          <thead className="sticky top-0 z-10 bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border px-1 py-1.5" title={t('common.related.header', 'Related records')} />
                  {visibleCols.map(col => {
                    if (col.key === 'clicked_count' || col.key === 'failed_count') {
                      return (
                        <th key={col.key} className="border-b border-csm-border px-3 py-1.5 csm-num"
                            style={widths[col.key] ? { width: widths[col.key], minWidth: widths[col.key] } : undefined}>
                          {col.header}
                        </th>
                      );
                    }
                    return (
                      <SortableHeader<StatSortKey> key={col.key}
                        sortKey={col.key as StatSortKey} label={col.header}
                        currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort}
                        style={widths[col.key] ? { width: widths[col.key], minWidth: widths[col.key] } : undefined} />
                    );
                  })}
                </tr>
          </thead>
          <tbody>
                {filtered.map(r => (
                  <Fragment key={r.campaign_id}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-1 py-1.5 text-center align-middle" onClick={e => e.stopPropagation()}>
                      <RelatedExpandButton
                        open={detail.isOpen(r.campaign_id)}
                        hasChildren={hasRelated(r.campaign_id)}
                        onToggle={() => detail.toggle(r.campaign_id)}
                        expandLabel={t('common.related.show', 'Show related records')}
                        collapseLabel={t('common.related.hide', 'Hide related')}
                        tooltip={t('common.related.tooltip', 'Show related records')}
                      />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className={`border-b border-csm-border-soft px-3 py-1.5${NUM_COLS.has(col.key) ? ' csm-num' : ''}`}
                          style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'campaign_name'    && <span className="font-medium max-w-[220px] truncate block">{r.campaign_name}</span>}
                        {col.key === 'status'           && (
                          <StatusBadge
                            variant={STATUS_META[r.status]?.variant ?? 'neutral'}
                            label={STATUS_META[r.status] ? t(STATUS_META[r.status].labelKey, STATUS_META[r.status].label) : r.status}
                          />
                        )}
                        {col.key === 'total_recipients' && <span className="text-right font-mono text-csm-sm block">{r.total_recipients.toLocaleString()}</span>}
                        {col.key === 'sent_count'       && <span className="text-right font-mono text-csm-sm block">{r.sent_count.toLocaleString()}</span>}
                        {col.key === 'delivered_pct'    && <PctBar pct={r.delivered_pct} color="#059669" />}
                        {col.key === 'bounce_pct'       && <PctBar pct={r.bounce_pct} color={r.bounce_pct > 5 ? '#DC2626' : r.bounce_pct > 2 ? '#D97706' : '#6B7280'} />}
                        {col.key === 'open_rate_pct'    && <PctBar pct={r.open_rate_pct} color="#00529B" />}
                        {col.key === 'clicked_count'    && <span className="text-right font-mono text-csm-sm block">{r.clicked_count.toLocaleString()}</span>}
                        {col.key === 'opted_out_count'  && (
                          <span className={`text-right font-mono text-csm-sm block ${r.opted_out_count > 0 ? 'text-csm-danger font-semibold' : 'text-csm-muted'}`}>
                            {r.opted_out_count.toLocaleString()}
                          </span>
                        )}
                        {col.key === 'failed_count'     && (
                          <span className={`text-right font-mono text-csm-sm block ${r.failed_count > 0 ? 'text-csm-warning font-semibold' : 'text-csm-muted'}`}>
                            {r.failed_count.toLocaleString()}
                          </span>
                        )}
                      </td>
                    ))}
                  </tr>
                  {detail.isOpen(r.campaign_id) && (
                    <tr>
                      <td colSpan={visibleCols.length + 1} className="border-b border-csm-border-soft bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                        <RelatedChildrenPanel groups={buildRelatedGroups(r.campaign_id)} session={session} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && (
                  <tr>
                    <td colSpan={visibleCols.length + 1} className="px-3 py-8 text-center">
                      <EmptyState
                        variant={stats.length === 0 ? 'no-data' : 'no-match'}
                        title={stats.length === 0
                          ? t('eds.campaigns.no_results')
                          : t('eds.dashboard.no_match', 'No campaigns match the current filter.')}
                      />
                    </td>
                  </tr>
                )}
          </tbody>
        </table>
          )}
        </PersonalizedGrid>
        )}
      </div>
    </div>
  );
}
